import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart3, Search, Building2, Network, Swords, Shield, LineChart, LayoutDashboard, BookOpen,
  ChevronDown, ChevronRight, Plus, Sun, Moon,
  Newspaper, Bell, HelpCircle, History, Loader2,
  Briefcase, Hash, Cpu, Globe, Bookmark, Table2, House, ChevronUp,
  Map as MapIcon,
} from 'lucide-react';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import ProfileDropdown from './ProfileDropdown';
import { useFollows } from '../hooks/useFollows';
import { useAnonFollows } from '../hooks/useAnonFollows';
import { useSavedSearches, requestApplySavedSearch } from '../hooks/useSavedSearches';

/**
 * SlackShell — GIS chrome. Fixed chrome; content gets
 * `lg:pt-12 lg:ml-[var(--gis-nav-w)]` (var set by App to the 68px icon rail).
 * Dark mode flows from the `gis-dark` class App puts on its root.
 *
 * IA: the ICON RAIL is the only chrome on the left, and the only place
 * destinations live (Deals/Search/Orgs/Ecosystem/Matchup/Research/Admin).
 * The contextual Following/Segments/Saved/Recent sidebar was retired
 * 2026-08-09 — see the note at its old render site.
 */

// Research leads. It was 8th of 8 — last before Admin — while being the only
// section that takes money. Measured over 60 days: 643 people arrived on a deal
// or company page from search and 3 of them ever reached /research. Position is
// the cheapest half of that fix; the other half is the offer now rendered on
// the deal and company pages themselves (api/_lib/report-promo.js).
// Redesign 2026-09-28 (handoff "GIS Home 2b"): the rail presents the site as
// three parts. Home, Research and Data are always visible; the app itself —
// Dash, Deals, Orgs, Signals, Search, Ecosystem, Matchup — sits under one
// collapsible "Platform" item, collapsed by default, open state remembered.
// Ecosystem and Matchup return to the nav here as part of Platform, per the
// handoff.
const RAIL_NAV = [
  { id: 'homepage', label: 'Home', icon: House },
  { id: 'research', label: 'Research', icon: BookOpen },
  // Data sits beside Research: the other thing the site sells.
  { id: 'data', label: 'Data', icon: Table2 },
  // Sherpa Atlas: infrastructure assets by country (public front page).
  { id: 'atlas', label: 'Atlas', icon: MapIcon },
];
const PLATFORM_NAV = [
  { id: 'home', label: 'Dash', icon: LayoutDashboard },
  { id: 'insights', label: 'Deals', icon: BarChart3 },
  { id: 'organizations', label: 'Orgs', icon: Building2 },
  { id: 'signals', label: 'Signals', icon: LineChart },
  { id: 'dashboard', label: 'Search', icon: Search },
  { id: 'ecosystem', label: 'Ecosystem', icon: Network },
  { id: 'matchup', label: 'Matchup', icon: Swords },
];
const PLATFORM_IDS = new Set([...PLATFORM_NAV.map((x) => x.id), 'deals', 'counterparts']);
const PLATFORM_KEY = 'gis-platform-open';

// Ecosystem and Matchup are out of the nav. They are not deleted — the tabs
// still render and the routes still work — but nothing offers them, so the
// first-visit rail is six items instead of eight.
//
// Two deliberate exceptions:
//   * admins keep them, so the pages can still be reached and checked
//   * whoever is already ON one keeps the item mounted, because MatchupPage
//     writes ?matchup=<ids> as its own share link and App.js honours it for
//     any visitor; without this a recipient loses the comparison the moment
//     they click anything else.
//
// Note this leaves SubscribePage and usageQuotas.js still advertising
// "10 ecosystem searches/month" to free users, and ecosystem/matchup quotas
// to every paid tier, with no route to spend them.
// (HIDDEN_TABS retired 2026-09-28: Ecosystem and Matchup are in PLATFORM_NAV.)

const SEGMENTS = [
  { name: 'Renewables', children: ['solar', 'onshore_wind', 'offshore_wind', 'hydro', 'storage', 'green_ammonia', 'biomass', 'geothermal', 'hybrid'] },
  { name: 'Digital', children: ['data_center', 'telecom'] },
  { name: 'Energy', children: ['gas', 'nuclear', 'oil_gas', 'transmission', 'hydrogen'] },
  { name: 'Transport', children: ['transport', 'ev_charging'] },
  { name: 'Industrial', children: ['industrial', 'mining', 'water_waste', 'agriculture', 'defense', 'hospitality'] },
];

const fmtTech = (t) => t === 'wind' ? 'Wind' : String(t).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\b(Ppa|Ipo|Epc|Ipp)\b/g, m => m.toUpperCase());

function SideSection({ label, count, open, onToggle, onAdd }) {
  return (
    <button onClick={onToggle}
      className="w-full flex items-center gap-1.5 px-2 py-1 mt-2.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--gis-side-section)] hover:text-[color:var(--gis-side-text-strong)]">
      <ChevronDown size={12} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && <span className="ml-auto font-bold opacity-70">{count}</span>}
      {onAdd && <Plus size={13} className={typeof count === 'number' && count > 0 ? 'ml-1.5' : 'ml-auto'} onClick={(e) => { e.stopPropagation(); onAdd(e); }} />}
    </button>
  );
}

// Contextual sidebar — interests only (Following / Segments / Recent). No
// destinations (those live in the icon rail), so nothing is duplicated.
function Sidebar({ go, counterparts, recentSearches, onSelectSegment, onRunSearch }) {
  const { follows, toggle, isSignedIn } = useFollows();
  const { removeAnonFollow } = useAnonFollows();
  const { searches: savedSearches, remove: removeSaved } = useSavedSearches();
  const [openFollow, setOpenFollow] = useState(true);
  const [openSeg, setOpenSeg] = useState(true);
  const [openSaved, setOpenSaved] = useState(true);
  const [openRecent, setOpenRecent] = useState(true);
  const [segState, setSegState] = useState({ Renewables: true });

  const followLabel = (f) => {
    const type = f.target_type || f.targetType;
    const id = f.target_id || f.targetId;
    if (type === 'counterpart') return (counterparts.find((c) => c.id === id)?.name) || f.label || 'Company';
    if (type === 'technology') return fmtTech(id);
    return id;
  };
  const followIcon = (f) => {
    const type = f.target_type || f.targetType;
    return type === 'counterpart' ? Building2 : type === 'technology' ? Cpu : Globe;
  };
  const rowCls = 'w-full flex items-center gap-2.5 pl-3 pr-2 py-[3px] rounded-md text-sm text-[color:var(--gis-side-text)] hover:bg-[color:var(--gis-side-hover)]';

  // Clicking a followed item scopes the Deals feed to it — same apply mechanism
  // saved searches use (counterpart → cps filter, technology → segment, country → geo).
  const applyFollow = (f) => {
    const type = f.target_type || f.targetType;
    const id = f.target_id || f.targetId;
    if (type === 'counterpart') requestApplySavedSearch({ cps: [id] });
    else if (type === 'technology') requestApplySavedSearch({ sectors: [id] });
    else if (type === 'country') requestApplySavedSearch({ geos: [id] });
    go('insights');
  };
  const unfollow = (f) => {
    const type = f.target_type || f.targetType;
    const id = f.target_id || f.targetId;
    if (isSignedIn) toggle(type, id); else removeAnonFollow(type, id);
  };

  return (
    <div className="h-full flex flex-col bg-[color:var(--gis-side-bg)] w-60">
      <div className="flex items-center gap-1 px-3 h-12 border-b border-[color:var(--gis-side-rule)] flex-shrink-0">
        <button className="flex items-center gap-1.5 min-w-0 flex-1 text-left" onClick={() => go('insights')}>
          <span className="text-[15px] font-extrabold leading-tight text-[color:var(--gis-side-text-strong)] truncate">Global Infrastructure Sherpa</span>
          <ChevronDown size={14} className="opacity-80 flex-shrink-0 text-[color:var(--gis-side-text)]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-1.5 custom-scrollbar">
        {/* Following */}
        <SideSection label="Following" count={follows.length} open={openFollow} onToggle={() => setOpenFollow((v) => !v)} />
        {openFollow && (follows.length > 0 ? follows.map((f, i) => {
          const Icon = followIcon(f);
          return (
            <div key={i} className="group/fl flex items-center gap-2.5 pl-3 pr-1 py-[3px] rounded-md text-sm text-[color:var(--gis-side-text)] hover:bg-[color:var(--gis-side-hover)]">
              <button onClick={() => applyFollow(f)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left" title={`Show ${followLabel(f)} deals`}>
                <Icon size={14} className="flex-shrink-0 opacity-80" />
                <span className="flex-1 text-left truncate">{followLabel(f)}</span>
              </button>
              <button onClick={() => unfollow(f)} title="Unfollow"
                className="flex-shrink-0 opacity-0 group-hover/fl:opacity-100 p-0.5 rounded hover:bg-white/10 text-[color:var(--gis-side-text-dim)]">×</button>
            </div>
          );
        }) : <p className="px-3 py-0.5 text-[11px] text-[color:var(--gis-side-text-dim)] leading-snug">Follow companies, sectors or countries to see them here.</p>)}

        {/* Segments — click filters the Deals feed to that sector */}
        <SideSection label="Segments" open={openSeg} onToggle={() => setOpenSeg((v) => !v)} />
        {openSeg && SEGMENTS.map((seg) => (
          <React.Fragment key={seg.name}>
            <button onClick={() => setSegState((s) => ({ ...s, [seg.name]: !s[seg.name] }))}
              className="w-full flex items-center gap-2 px-2.5 py-[3px] rounded-md text-sm font-semibold text-[color:var(--gis-side-text)] hover:bg-[color:var(--gis-side-hover)]">
              <ChevronDown size={12} className={`transition-transform ${segState[seg.name] ? '' : '-rotate-90'}`} />
              <span className="truncate">{seg.name}</span>
            </button>
            {segState[seg.name] && seg.children.map((c) => (
              <button key={c} onClick={() => onSelectSegment?.(c)} title={`Show ${fmtTech(c)} deals`}
                className="w-full flex items-center gap-1.5 pl-7 pr-2 py-[3px] rounded-md text-sm text-[color:var(--gis-side-text)] hover:bg-[color:var(--gis-side-hover)]">
                <Hash size={13} className="flex-shrink-0 opacity-60" />
                <span className="flex-1 text-left truncate">{fmtTech(c)}</span>
              </button>
            ))}
          </React.Fragment>
        ))}

        {/* Saved searches — named filter sets; click applies, × deletes */}
        <SideSection label="Saved" count={savedSearches.length} open={openSaved} onToggle={() => setOpenSaved((v) => !v)} />
        {openSaved && (savedSearches.length > 0 ? savedSearches.map((s) => (
          <div key={s.id} className="group/sv flex items-center gap-2.5 pl-3 pr-1 py-[3px] rounded-md text-sm text-[color:var(--gis-side-text)] hover:bg-[color:var(--gis-side-hover)]">
            <button onClick={() => { requestApplySavedSearch(s.filters); go('insights'); }} className="flex items-center gap-2.5 flex-1 min-w-0 text-left" title={`Apply "${s.name}"`}>
              <Bookmark size={13} className="flex-shrink-0 opacity-70" />
              <span className="flex-1 text-left truncate">{s.name}</span>
            </button>
            <button onClick={() => removeSaved(s.id)} title="Delete saved search"
              className="flex-shrink-0 opacity-0 group-hover/sv:opacity-100 p-0.5 rounded hover:bg-white/10 text-[color:var(--gis-side-text-dim)]">×</button>
          </div>
        )) : <p className="px-3 py-0.5 text-[11px] text-[color:var(--gis-side-text-dim)]">Save a Live Feed search to see it here.</p>)}

        {/* Recent searches — click re-runs the search */}
        <SideSection label="Recent" open={openRecent} onToggle={() => setOpenRecent((v) => !v)} />
        {openRecent && (recentSearches.length > 0 ? recentSearches.slice(0, 6).map((s, i) => (
          <button key={i} onClick={() => onRunSearch?.(s)} className={rowCls} title={`Search "${s}"`}>
            <Bookmark size={13} className="flex-shrink-0 opacity-70" />
            <span className="flex-1 text-left truncate">{s}</span>
          </button>
        )) : <p className="px-3 py-0.5 text-[11px] text-[color:var(--gis-side-text-dim)]">Your recent searches appear here.</p>)}
      </div>
    </div>
  );
}

// Reports and data files, for the search box. Fetched once, the first time the
// box is used — the catalogue is small, so matching happens in the browser.
let catalogueCache = null;
async function loadCatalogue() {
  if (catalogueCache) return catalogueCache;
  const get = (u) => fetch(u).then((r) => (r.ok ? r.json() : { reports: [] })).catch(() => ({ reports: [] }));
  const [r, d] = await Promise.all([get('/api/research'), get('/api/research?type=Dataset')]);
  catalogueCache = [
    ...(r.reports || []).map((x) => ({ ...x, _kind: 'report' })),
    ...(d.reports || []).map((x) => ({ ...x, _kind: 'data' })),
  ];
  return catalogueCache;
}

function TopFrame(props) {
  const { searchQuery, setSearchQuery, showSuggestions, setShowSuggestions, suggestions, recentSearches, searchLoading, onSearchSubmit, onSuggestionSelect, onSmartSearch, setActiveTab, onOpenReport, onOpenData } = props;
  const searchRef = useRef(null);
  const [catalogue, setCatalogue] = useState([]);
  const warmCatalogue = () => { if (!catalogue.length) loadCatalogue().then(setCatalogue); };
  const sq = (searchQuery || '').trim().toLowerCase();
  const titleHits = sq.length < 2 ? [] : catalogue
    .filter((x) => [x.title, x.subtitle, x.geography, x.category, x.edition].filter(Boolean).join(' ').toLowerCase().includes(sq))
    .slice(0, 4);
  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions?.(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [setShowSuggestions]);

  return (
    <div className="h-12 flex items-center gap-2 px-3 bg-[color:var(--gis-frame-bg)]">
      <div className="flex-1 max-w-[560px] mx-auto" ref={searchRef}>
        <form onSubmit={onSearchSubmit} className="relative">
          <div className="relative flex items-center h-8 rounded-md border border-[#3d5a52] bg-white/5 hover:bg-white/10 focus-within:bg-white/12 focus-within:border-[#5b7a70] transition-colors">
            <Search size={15} className="ml-2.5 text-white/60 flex-shrink-0" />
            <input type="text" value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); warmCatalogue(); }}
              onFocus={() => { setShowSuggestions(true); warmCatalogue(); }}
              placeholder="Search reports, datasets, deals and companies"
              className="flex-1 h-full px-2 text-sm bg-transparent border-0 outline-none text-white placeholder-white/55" />
            {searchLoading && <Loader2 size={14} className="animate-spin mr-2.5 text-white/70" />}
          </div>
          {showSuggestions && (() => {
            const sl = searchQuery.toLowerCase();
            const matchedRecent = searchQuery ? recentSearches.filter((q) => q.toLowerCase().includes(sl)).slice(0, 3) : recentSearches.slice(0, 5);
            const hasRecent = matchedRecent.length > 0;
            const hasSug = searchQuery && suggestions.length > 0;
            const hasTitles = titleHits.length > 0;
            if (!hasRecent && !hasSug && !hasTitles) return null;
            return (
              <div className="absolute z-50 w-full mt-1 rounded-xl shadow-xl border overflow-hidden text-left bg-[color:var(--gis-panel)] border-[color:var(--gis-rule)]">
                {hasTitles && (<>
                  <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--gis-dim)]">Reports and data</div>
                  {titleHits.map((x) => (
                    <button type="button" key={`t-${x.id}`}
                      onClick={() => { setShowSuggestions(false); setSearchQuery(''); (x._kind === 'data' ? onOpenData : onOpenReport)?.(x.slug); }}
                      className="w-full px-4 py-2 flex items-center gap-2.5 hover:bg-[color:var(--gis-bg)] text-sm text-left">
                      {x._kind === 'data' ? <Table2 size={13} className="text-[#1d6f42]" /> : <BookOpen size={13} className="text-[color:var(--gis-dim)]" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate text-[color:var(--gis-ink)]">{x.title}</p>
                        <p className="text-xs text-[color:var(--gis-dim)] truncate">{[x._kind === 'data' ? 'Dataset' : 'Report', x.edition, x.geography].filter(Boolean).join(' · ')}</p>
                      </div>
                      <ChevronRight size={12} className="text-[color:var(--gis-dim)] opacity-60" />
                    </button>
                  ))}
                  {(hasRecent || hasSug) && <div className="border-t border-[color:var(--gis-rule)]" />}
                </>)}
                {hasRecent && (<>
                  <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--gis-dim)]">Recent</div>
                  {matchedRecent.map((q, i) => (
                    <button type="button" key={`r-${i}`} onClick={() => { setSearchQuery(q); setShowSuggestions(false); onSmartSearch(q); }} className="w-full px-4 py-2 flex items-center gap-2.5 hover:bg-[color:var(--gis-bg)] text-sm">
                      <History size={13} className="text-[color:var(--gis-dim)]" /><span className="text-[color:var(--gis-body)]">{q}</span>
                    </button>
                  ))}
                </>)}
                {hasSug && (<>
                  {hasRecent && <div className="border-t border-[color:var(--gis-rule)]" />}
                  <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--gis-dim)]">Matches</div>
                  {suggestions.map((item) => (
                    <button type="button" key={`${item.kind}-${item.id}`} onClick={() => onSuggestionSelect(item)} className="w-full px-4 py-2 flex items-center gap-2.5 hover:bg-[color:var(--gis-bg)] text-sm">
                      {item.kind === 'deal' ? <Briefcase size={13} className="text-[color:var(--gis-accent)]" /> : <Building2 size={13} className="text-[color:var(--gis-dim)]" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate text-[color:var(--gis-ink)]">{item.name}</p>
                        <p className="text-xs text-[color:var(--gis-dim)]">{item.kind === 'deal' ? [item.type, item.country].filter(Boolean).join(' · ') : <>{item.category || item.type}{item.dealCount > 0 ? ` · ${item.dealCount} deal${item.dealCount !== 1 ? 's' : ''}` : ''}</>}</p>
                      </div>
                      <ChevronRight size={12} className="text-[color:var(--gis-dim)] opacity-60" />
                    </button>
                  ))}
                </>)}
                {searchQuery.trim() && (<>
                  <div className="border-t border-[color:var(--gis-rule)]" />
                  <button onClick={onSearchSubmit} className="w-full px-4 py-2 text-sm font-medium text-[color:var(--gis-accent)] hover:bg-[color:var(--gis-bg)]">Search all for "{searchQuery}" →</button>
                </>)}
              </div>
            );
          })()}
        </form>
      </div>
      <div className="flex items-center gap-1.5 text-white/65">
        {/* Redesign 2026-09-28: a visitor sees "Sign in" and the Platform offer;
            a signed-in user keeps the newsletter, alerts and help icons. */}
        <SignedOut>
          <SignInButton mode="modal"><button type="button" className="px-2.5 py-1 text-[13px] font-medium text-[#dfe6e3] hover:text-white rounded">Sign in</button></SignInButton>
          <button type="button" onClick={() => setActiveTab?.('subscribe')} className="px-3 py-1.5 text-[13px] font-semibold rounded-md hover:brightness-105" style={{ background: '#d9b86a', color: '#1f3b34' }}>Subscribe to Platform</button>
        </SignedOut>
        <SignedIn>
          <a href="https://globalinfrastructuresherpa.substack.com/" target="_blank" rel="noopener noreferrer" title="Newsletter" className="p-1.5 rounded hover:bg-white/10"><Newspaper size={16} /></a>
          <button onClick={() => setActiveTab?.('alerts')} className="p-1.5 rounded hover:bg-white/10" title="My Alerts"><Bell size={16} /></button>
          <a href="/help" title="Help Center" className="p-1.5 rounded hover:bg-white/10"><HelpCircle size={16} /></a>
        </SignedIn>
      </div>
    </div>
  );
}

export default function SlackShell({
  activeTab, setActiveTab,
  theme = 'light', setTheme, userTier, user, isAdmin = false,
  onLogoClick,
  onOpenReport,
  onOpenData,
  searchQuery = '', setSearchQuery, showSuggestions = false, setShowSuggestions,
  suggestions = [], recentSearches = [], searchLoading = false,
  onSearchSubmit, onSuggestionSelect, onSmartSearch,
  counterparts = [],
  collapsed = false, setCollapsed,
  onSelectSegment, onRunSearch,
  t = (k) => k,
}) {
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const go = (tab) => { setActiveTab(tab); };
  const railNav = RAIL_NAV;
  const [platformOpen, setPlatformOpen] = useState(() => {
    try { return localStorage.getItem(PLATFORM_KEY) === '1'; } catch { return false; }
  });
  const togglePlatform = () => setPlatformOpen((v) => {
    try { localStorage.setItem(PLATFORM_KEY, v ? '0' : '1'); } catch { /* private mode */ }
    return !v;
  });
  // Being on a Platform page always shows where you are, whatever was saved.
  const platformActive = PLATFORM_IDS.has(activeTab);
  const showPlatform = platformOpen || platformActive;

  return (
    <>
      {/* Icon rail (lg+) */}
      <div className="hidden lg:flex flex-col items-center fixed top-0 left-0 z-50 w-[68px] h-screen overflow-y-auto [scrollbar-width:none] bg-[color:var(--gis-frame-bg)] py-2 gap-0.5">
        {/* The rail carried the letters "GIS" on a brand-coloured square, not the
            mark itself — a placeholder that outlived its purpose. logo512.png is
            the real thing and has always been served. The lettering stays as the
            onError fallback so a broken asset degrades to what was here before
            rather than to an empty square. */}
        <button onClick={onLogoClick} title="Global Infrastructure Sherpa"
          className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center text-white text-[11px] font-extrabold mb-1 shrink-0"
          style={{ background: 'var(--gis-brand)' }}>
          <img src="/logo512.png" alt="Global Infrastructure Sherpa" width="36" height="36"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentNode.textContent = 'GIS'; }} />
        </button>
        {railNav.map(({ id, label, icon: Icon }) => {
          const active = activeTab === id;
          return (
            <button key={id} onClick={() => go(id)} title={label}
              className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${active ? 'text-white' : 'text-white/55 hover:text-white/90'}`}>
              <span className={`flex items-center justify-center w-9 h-7 rounded-lg ${active ? 'bg-white/18' : 'hover:bg-white/8'}`}>
                <Icon size={20} strokeWidth={active ? 2.1 : 1.9} />
              </span>
              <span className="text-[10px] leading-none">{label}</span>
            </button>
          );
        })}
        <div className="w-12 h-px bg-white/15 my-1.5 shrink-0" />
        {/* Platform: the subscription app, one collapsible group. */}
        <button onClick={togglePlatform} title={showPlatform ? 'Hide Platform' : 'Show Platform'} aria-expanded={showPlatform}
          className={`relative w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${platformActive ? 'text-white' : 'text-white/55 hover:text-white/90'}`}>
          <span className="relative flex items-center justify-center w-9 h-7 rounded-lg hover:bg-white/8">
            <LayoutDashboard size={20} strokeWidth={1.9} />
            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: '#d9b86a' }} />
          </span>
          <span className="text-[10px] leading-none">Platform</span>
          {showPlatform ? <ChevronUp size={11} className="opacity-70" /> : <ChevronDown size={11} className="opacity-70" />}
        </button>
        {showPlatform && (
          <div className="w-full flex flex-col items-center gap-0.5 py-1 rounded-lg" style={{ background: '#19322c' }}>
            {PLATFORM_NAV.map(({ id, label, icon: Icon }) => {
              const active = activeTab === id || (id === 'insights' && (activeTab === 'deals' || activeTab === 'counterparts'));
              return (
                <button key={id} onClick={() => go(id)} title="Included with a Platform subscription"
                  className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${active ? 'text-white' : 'text-[#a9bab4] hover:text-white'}`}>
                  <span className={`flex items-center justify-center w-8 h-6 rounded-lg ${active ? 'bg-white/18' : 'hover:bg-white/8'}`}>
                    <Icon size={16} strokeWidth={active ? 2.1 : 1.9} />
                  </span>
                  <span className="text-[9.5px] leading-none">{label}</span>
                </button>
              );
            })}
          </div>
        )}
        {isAdmin && (
          <button onClick={() => go('admin')} title="Admin"
            className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${activeTab === 'admin' ? 'text-white' : 'text-white/55 hover:text-white/90'}`}>
            <span className={`flex items-center justify-center w-9 h-7 rounded-lg ${activeTab === 'admin' ? 'bg-white/18' : 'hover:bg-white/8'}`}>
              <Shield size={20} strokeWidth={activeTab === 'admin' ? 2.1 : 1.9} />
            </span>
            <span className="text-[10px] leading-none">Admin</span>
          </button>
        )}
        <div className="flex-1" />
        <div className="text-[9px] leading-none text-[#8fa59e] flex items-center gap-1 mb-1" title="Platform is included with a subscription">
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#d9b86a' }} />Subscribers
        </div>
        <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} title="Toggle theme" className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/8">
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <SignedIn>
          <div ref={profileRef} className="relative">
            <button onClick={() => setShowProfile((v) => !v)} className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: 'var(--gis-presence)' }}>
              {user?.firstName?.charAt(0) || user?.emailAddresses?.[0]?.emailAddress?.charAt(0)?.toUpperCase() || 'U'}
            </button>
            {showProfile && (
              // Fixed to the screen, not the rail: the rail scrolls
              // (overflow-y-auto), which crops anything positioned outside it
              // and left the account menu hidden behind the page.
              <div className="fixed bottom-3 left-[76px] z-[60]">
                <ProfileDropdown user={user} theme={theme} setTheme={setTheme} userTier={userTier} onNavigate={(tab) => { setActiveTab(tab); setShowProfile(false); }} onClose={() => setShowProfile(false)} t={t} />
              </div>
            )}
          </div>
        </SignedIn>
        <SignedOut>
          <SignInButton mode="modal"><button className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center text-xs font-semibold hover:bg-white/25" title="Sign in">in</button></SignInButton>
        </SignedOut>
      </div>

      {/* Contextual sidebar — RETIRED 2026-08-09.
          The Following / Segments / Saved / Recent panel is no longer rendered
          anywhere in the app. The icon rail is now the only chrome on the left,
          so every surface gets ~240px more width. The <Sidebar> component and
          its data hooks are intentionally left in place: nothing else renders
          it, and keeping it costs nothing but makes restoring the panel a
          one-line change rather than an archaeology exercise. */}

      {/* Top frame (lg+) — left offset tracks nav width */}
      <div className="hidden lg:block fixed top-0 right-0 z-30 left-[var(--gis-nav-w)]">
        <TopFrame searchQuery={searchQuery} setSearchQuery={setSearchQuery} showSuggestions={showSuggestions} setShowSuggestions={setShowSuggestions}
          suggestions={suggestions} recentSearches={recentSearches} searchLoading={searchLoading}
          onSearchSubmit={onSearchSubmit} onSuggestionSelect={onSuggestionSelect} onSmartSearch={onSmartSearch} setActiveTab={setActiveTab}
          onOpenReport={onOpenReport} onOpenData={onOpenData} />
      </div>

      {/* Mobile: chrome is desktop-only (lg+); phones use MobileBottomNav (App.js). */}
    </>
  );
}
